import { z } from "zod";
import { RenderStatusSchema } from "./book";
import { FailureSchema, IsoDateTimeSchema, MoneySchema, ProgressSchema } from "./common";
import { BookIdSchema, ChapterIdSchema, JobIdSchema } from "./ids";
import { JobSummarySchema } from "./jobs";

export const RENDER_CHAPTER_STATUSES = [
  "queued",
  "rendering",
  "rendered",
  /** Failed, automatic retry scheduled at `retryAt`. */
  "retrying",
  /** Failed three times; waits for POST /jobs/:id/retry. */
  "failed",
  "stopped",
  /** Not part of this run: had current Chapter Audio already, or is excluded. */
  "skipped",
] as const;
export const RenderChapterStatusSchema = z.enum(RENDER_CHAPTER_STATUSES);
export type RenderChapterStatus = z.infer<typeof RenderChapterStatusSchema>;

export const RenderChapterProgressSchema = z.strictObject({
  chapterId: ChapterIdSchema,
  position: z.int().positive(),
  number: z.int().positive(),
  title: z.string(),
  status: RenderChapterStatusSchema,
  /** Blocks done of total while rendering. */
  progress: ProgressSchema.nullable(),
  characters: z.int().nonnegative(),
  charactersDone: z.int().nonnegative(),
  spent: MoneySchema,
  durationMs: z.int().nonnegative().nullable(),
  renderedAt: IsoDateTimeSchema.nullable(),
  attempt: z.int().nonnegative(),
  maxAttempts: z.int().positive(),
  retryAt: IsoDateTimeSchema.nullable(),
  failure: FailureSchema.nullable(),
  jobId: JobIdSchema.nullable(),
});
export type RenderChapterProgress = z.infer<typeof RenderChapterProgressSchema>;

export const SpentSchema = z.strictObject({
  characters: z.int().nonnegative(),
  credits: z.int().nonnegative(),
  money: MoneySchema,
  /** Audio produced (spent) or projected (estimated). */
  durationMs: z.int().nonnegative(),
});
export type Spent = z.infer<typeof SpentSchema>;

export const STOP_MODES = ["afterChapter", "immediately"] as const;
export const StopModeSchema = z.enum(STOP_MODES);
export type StopMode = z.infer<typeof StopModeSchema>;

/** POST /books/:id/render/stop. */
export const StopRequestSchema = z.strictObject({ mode: StopModeSchema });
export type StopRequest = z.infer<typeof StopRequestSchema>;

/**
 * GET /books/:id/render: the progress screen. `status` idle means no render
 * was ever started; the chapter rows still list every included Chapter.
 */
export const RenderProgressSchema = z.strictObject({
  bookId: BookIdSchema,
  status: RenderStatusSchema,
  startedAt: IsoDateTimeSchema.nullable(),
  finishedAt: IsoDateTimeSchema.nullable(),
  chapters: z.array(RenderChapterProgressSchema),
  chaptersDone: z.int().nonnegative(),
  chaptersTotal: z.int().nonnegative(),
  spent: SpentSchema,
  estimated: SpentSchema,
  etaSeconds: z.int().nonnegative().nullable(),
  stopRequested: StopModeSchema.nullable(),
  /** The assembly job once the last Chapter Audio arrived. */
  assembly: JobSummarySchema.nullable(),
});
export type RenderProgress = z.infer<typeof RenderProgressSchema>;
